import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit, Optional, Type } from '@angular/core';
import { BlockingLoadingSpinnerComponent, RefreshService } from '@softline/application';
import {
  AuthenticationContextStore,
  SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
} from '@softline/auth';
import { Store } from '@softline/core';
import { IsDefinedNotEmptyPipe, LegacyModalOption } from '@softline/ui-core';
import { combineLatest, Observable } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import {
  SOFTLINE_CONFIG_EDIT_CONTEXT_COMPONENT,
  SOFTLINE_FEATURE_UTILITIES_FIRMA,
  SOFTLINE_FEATURE_UTILITIES_VKFORGS,
} from '../../../../utilities.shared';
import { FirmaStore } from '../../../firma.store';
import { PatchContextService } from '../../../services/patch-context.service';
import { VkforgStore } from '../../../vkforg.store';
import { Vkforg } from '../../data/vkforg';

@Component({
  selector: 'soft-account-context-widget',
  imports: [CommonModule, BlockingLoadingSpinnerComponent, IsDefinedNotEmptyPipe],
  templateUrl: './vkforg-context-widget.component.html',
  styleUrls: ['./vkforg-context-widget.component.css'],
})
export class VkforgContextWidgetComponent implements OnInit {
  readonly vkforgs$ = this.store.observe(
    SOFTLINE_FEATURE_UTILITIES_VKFORGS,
    VkforgStore.getters.all
  );

  readonly firmen$ = this.store.observe(
    SOFTLINE_FEATURE_UTILITIES_FIRMA,
    FirmaStore.getters.all
  );

  readonly context$ = this.store.observe(
    SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
    AuthenticationContextStore.getters.data
  ) as Observable<any>;

  readonly vkforg$ = combineLatest([this.vkforgs$, this.context$]).pipe(
    map(([o, context]) => o.find((v) => v.id === context?.vkforgid) ?? o[0])
  );

  readonly firma$ = combineLatest([this.firmen$, this.vkforg$]).pipe(
    map(([firmen, vkforg]) => firmen.find((o) => o?.id === vkforg?.idfirma))
  );

  readonly user$ = this.context$.pipe(
    switchMap((context) =>
      this.store.observe(
        SOFTLINE_FEATURE_AUTHENTICATION_CONTEXT,
        AuthenticationContextStore.getters.data,
        context as any
      )
    )
  );

  constructor(
    private store: Store,
    private refreshService: RefreshService,
    readonly patchContextService: PatchContextService,
    @Optional()
    @Inject(SOFTLINE_CONFIG_EDIT_CONTEXT_COMPONENT)
    public components?: Type<Component>[]
  ) {}

  async ngOnInit(): Promise<void> {
    await Promise.all([
      await this.store.dispatch(
        SOFTLINE_FEATURE_UTILITIES_VKFORGS,
        VkforgStore.actions.loadMany,
        { clear: true }
      ),
      await this.store.dispatch(
        SOFTLINE_FEATURE_UTILITIES_FIRMA,
        FirmaStore.actions.loadMany,
        { clear: true }
      ),
    ]);
  }

  async onChangeAccount(vkforgs: Vkforg[]): Promise<void> {
    const options = vkforgs.map<LegacyModalOption<string>>((o) => ({
      value: o?.id + '',
      label:
        o?.kurzbezeichnung?.length < 1
          ? o?.vkforg
          : ((o?.kurzbezeichnung || o?.vkforg) ?? ''),
    }));

    if (this.components && this.components?.length > 1) {
      options.push({
        value: 'FIRMENWECHSEL_OEFFNEN',
        label: '#UTILITIES.CONTEXT.DETAIL_CONTEXT_SETTINGS',
        icon: 'fa-regular fa-user-gear',
      });
    }

    await this.patchContextService.patchContextOptionsDialog<string>({
      title: 'Verkaufsorganisation auswählen',
      options,
      editContextDialogOptionValue: 'FIRMENWECHSEL_OEFFNEN',
      setOnSelect: (selectedVkforgId) => {
        const vkforg = this.store.get(
          SOFTLINE_FEATURE_UTILITIES_VKFORGS,
          VkforgStore.getters.entity,
          +selectedVkforgId
        );

        if (!vkforg) return null;

        return {
          vkforgid: +selectedVkforgId,
          firmaid: vkforg.idfirma,
        };
      },
    });
  }
}
